
import { useState } from 'react';
import type { AIProjectComment } from '../../../../mocks/aiProjects';
import { useAuth } from '../../../../contexts/AuthContext';
import { AuthModal } from '../../../../components/feature/AuthModal';

interface ProjectCommentsProps {
  comments: AIProjectComment[];
  totalCount: number;
}

const ProjectComments = ({ comments, totalCount }: ProjectCommentsProps) => {
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState<'newest' | 'popular'>('popular');
  const [newComment, setNewComment] = useState('');
  const [likedComments, setLikedComments] = useState<Set<number>>(new Set());
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyText, setReplyText] = useState('');
  const [localComments, setLocalComments] = useState<AIProjectComment[]>(comments);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const sortedComments = [...localComments].sort((a, b) => {
    if (sortBy === 'popular') return b.likes - a.likes;
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const handleLike = (commentId: number) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setLikedComments(prev => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  };

  const handleSubmitComment = () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!newComment.trim()) return;
    const comment: AIProjectComment = {
      id: Date.now(),
      user: 'You',
      avatar:
        'https://public.readdy.ai/ai/img_res/6c382b9d352ecf3c25a438337756d6a3.jpg',
      comment: newComment,
      date: new Date().toISOString().split('T')[0],
      likes: 0,
      replies: [],
    };
    setLocalComments([comment, ...localComments]);
    setNewComment('');
  };

  const handleReplyClick = (commentId: number) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    setReplyingTo(replyingTo === commentId ? null : commentId);
  };

  const handleSubmitReply = (parentId: number) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (!replyText.trim()) return;
    const reply: AIProjectComment = {
      id: Date.now(),
      user: 'You',
      avatar:
        'https://readdy.ai/api/search-image?query=Default%20user%20avatar%20neutral%20expression%20simple%20modern%20style%20clean%20white%20background&width=80&height=80&seq=default-user-av&orientation=squarish',
      comment: replyText,
      date: new Date().toISOString().split('T')[0],
      likes: 0,
      replies: [],
    };
    setLocalComments(prev =>
      prev.map(c => (c.id === parentId ? { ...c, replies: [...c.replies, reply] } : c))
    );
    setReplyText('');
    setReplyingTo(null);
  };

  const renderComment = (comment: AIProjectComment, isReply = false) => {
    const liked = likedComments.has(comment.id);
    return (
      <div key={comment.id} className={`${isReply ? 'ml-12 mt-3' : ''}`}>
        <div className="flex gap-3">
          <img
            src={comment.avatar}
            alt={comment.user}
            className="w-9 h-9 rounded-full object-cover flex-shrink-0 border-2 border-slate-700"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-white">{comment.user}</span>
              <span className="text-xs text-slate-500">{comment.date}</span>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed mb-2">{comment.comment}</p>
            <div className="flex items-center gap-4">
              <button
                onClick={() => handleLike(comment.id)}
                className={`flex items-center gap-1 text-xs transition-colors cursor-pointer ${
                  liked ? 'text-rose-400' : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                <i className={`${liked ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                {comment.likes + (liked ? 1 : 0)}
              </button>
              {!isReply && (
                <button
                  onClick={() => handleReplyClick(comment.id)}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors cursor-pointer"
                >
                  <i className="ri-reply-line"></i>
                  Reply
                </button>
              )}
            </div>

            {/* Reply Input */}
            {replyingTo === comment.id && user && (
              <div className="mt-3 flex gap-2">
                <input
                  type="text"
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Write a reply..."
                  className="flex-1 px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-sm text-white placeholder-slate-400 focus:outline-none focus:border-orange-500"
                  onKeyDown={e => e.key === 'Enter' && handleSubmitReply(comment.id)}
                />
                <button
                  onClick={() => handleSubmitReply(comment.id)}
                  disabled={!replyText.trim()}
                  className="px-3 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-600 text-white text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Reply
                </button>
              </div>
            )}

            {/* Replies */}
            {comment.replies.length > 0 && (
              <div className="space-y-3 mt-3">
                {comment.replies.map(reply => renderComment(reply, true))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-5 border-b border-slate-700 flex items-center justify-between">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <i className="ri-chat-3-line text-orange-400"></i>
          Comments
          <span className="text-sm font-normal text-slate-400">({totalCount})</span>
        </h3>
        <div className="flex items-center gap-1 bg-slate-700/50 rounded-lg p-1">
          <button
            onClick={() => setSortBy('popular')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'popular' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Popular
          </button>
          <button
            onClick={() => setSortBy('newest')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
              sortBy === 'newest' ? 'bg-orange-500 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            Newest
          </button>
        </div>
      </div>

      {/* New Comment */}
      <div className="p-5 border-b border-slate-700">
        {user ? (
          <div className="flex gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-white text-sm"></i>
            </div>
            <div className="flex-1">
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Share your thoughts on this project..."
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-sm text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 resize-none"
                rows={3}
                maxLength={500}
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-slate-500">{newComment.length}/500</span>
                <button
                  onClick={handleSubmitComment}
                  disabled={!newComment.trim()}
                  className="px-5 py-2 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 disabled:from-slate-600 disabled:to-slate-600 text-white text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
                >
                  Post Comment
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-700/50 flex items-center justify-center">
              <i className="ri-lock-line text-xl text-slate-400"></i>
            </div>
            <p className="text-sm text-slate-400 mb-3">Sign in to join the conversation</p>
            <button
              onClick={() => setShowAuthModal(true)}
              className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-600 hover:to-rose-600 text-white text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-login-box-line mr-2"></i>
              Sign In
            </button>
          </div>
        )}
      </div>

      {/* Comments List */}
      <div className="p-5 space-y-5">
        {sortedComments.length > 0 ? (
          sortedComments.map(comment => renderComment(comment))
        ) : (
          <div className="text-center py-8">
            <i className="ri-chat-3-line text-4xl text-slate-600 mb-3"></i>
            <p className="text-slate-400 text-sm">
              No comments yet. Be the first to share your thoughts!
            </p>
          </div>
        )}
      </div>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode="login"
      />
    </div>
  );
};

export default ProjectComments;
